import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, TrendingUp } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { formatPrice } from "@/lib/api";

const fmt = (v) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(v);

const Slider = ({ label, value, min, max, onChange, testid, unit = "€" }) => (
  <div>
    <div className="flex items-center justify-between">
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{label}</label>
      <span data-testid={`${testid}-value`} className="font-display text-lg font-bold text-electric">
        {unit === "€" ? `${fmt(value)} ${unit}` : `${value} ${unit}`}
      </span>
    </div>
    <input
      data-testid={testid}
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="mt-3 w-full cursor-pointer appearance-none rounded-full bg-surface accent-[#38BDF8] [&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-[#172847] [&::-webkit-slider-thumb]:mt-[-6px] [&::-webkit-slider-thumb]:h-[18px] [&::-webkit-slider-thumb]:w-[18px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-electric"
    />
  </div>
);

export const CalcolatoreROI = () => {
  const [videos, setVideos] = useState(4);
  const [hours, setHours] = useState(6);
  const [rate, setRate] = useState(40);

  const { internalCost, studioCost, saving } = useMemo(() => {
    const internal = videos * hours * rate;
    const studio = videos * 220 + 900;
    return { internalCost: internal, studioCost: studio, saving: internal - studio };
  }, [videos, hours, rate]);

  return (
    <div className="pt-28 lg:pt-36 pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Tool interattivo</Eyebrow>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            Quanto costa <span className="text-gradient-blue">far video in casa?</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-400">
            Muovi i cursori per stimare il costo di una produzione video
            interna e confrontalo con un pacchetto dedicato del nostro studio.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <Reveal className="rounded-3xl border border-lineblue bg-panel p-8 sm:p-10">
            <Calculator className="h-8 w-8 text-electric" />
            <h2 className="mt-5 font-display text-2xl font-semibold text-white">
              La tua produzione interna
            </h2>
            <div className="mt-10 space-y-9">
              <Slider
                label="Video al mese"
                value={videos}
                min={1}
                max={20}
                unit="pz"
                onChange={setVideos}
                testid="roi-videos-per-month"
              />
              <Slider
                label="Ore di lavoro per video"
                value={hours}
                min={1}
                max={12}
                unit="h"
                onChange={setHours}
                testid="roi-hours-per-video"
              />
              <Slider
                label="Costo orario interno"
                value={rate}
                min={15}
                max={120}
                unit="€"
                onChange={setRate}
                testid="roi-hourly-rate"
              />
            </div>
          </Reveal>

          <Reveal delay={0.12} className="flex flex-col rounded-3xl border border-glow/30 bg-gradient-to-b from-panel to-surface p-8 sm:p-10">
            <TrendingUp className="h-8 w-8 text-electric" />
            <h2 className="mt-5 font-display text-2xl font-semibold text-white">
              Stima del confronto
            </h2>
            <div className="mt-10 flex-1 space-y-6">
              <div className="rounded-2xl border border-lineblue bg-ink/60 p-6">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">
                  Costo mensile interno
                </p>
                <p data-testid="roi-internal-cost" className="mt-2 font-display text-3xl font-bold text-slate-200">
                  {formatPrice(internalCost)}
                </p>
              </div>
              <div className="rounded-2xl border border-glow/30 bg-ink/60 p-6">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">
                  Pacchetto mensile CINEBLUE (stima)
                </p>
                <p data-testid="roi-studio-cost" className="mt-2 font-display text-3xl font-bold text-white">
                  {formatPrice(studioCost)}
                </p>
              </div>
              <div
                data-testid="roi-saving"
                className={`rounded-2xl p-6 text-center ${
                  saving > 0 ? "bg-electric/10 border border-electric/40" : "bg-cobalt/10 border border-cobalt/40"
                }`}
              >
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">
                  {saving > 0 ? "Risparmio stimato al mese" : "Differenza stimata al mese"}
                </p>
                <p className={`mt-2 font-display text-4xl font-bold ${saving > 0 ? "text-electric" : "text-slate-300"}`}>
                  {formatPrice(Math.abs(saving))}
                </p>
              </div>
            </div>
            <p className="mt-6 text-center text-[11px] leading-relaxed text-slate-500">
              Stima indicativa e non vincolante, a puro titolo dimostrativo.
            </p>
            <Link
              to="/pacchetti"
              data-testid="roi-cta-packages"
              className="group mt-6 inline-flex items-center justify-center gap-3 rounded-full bg-electric px-7 py-4 text-sm font-bold text-ink transition-all duration-300 hover:bg-glow"
            >
              Scegli un pacchetto
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default CalcolatoreROI;
